import React, { useState } from 'react'
import styled from 'styled-components'
import { ComponentStyleSheet } from '@nexiahome/ui-common/themes'

import { Button, type ButtonTheme } from '@nexiahome/ui-common/components/primitives/buttons'
import CollapsiblePanel from 'components/CollapsiblePanel'

import useScreenSize from 'hooks/useScreenSize'

import Api from 'services/api'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import blobToFileDownload from 'helpers/blobToFileDownload'

import { ProductLiterature } from 'types/ProductLiterature'

type Props = {
  group: ProductLiterature
}

const ProductLiteratureGroup = ({ group }: Props) => {
  const [displayedLiterature, setDisplayedLiterature] = useState(group.literature.slice(0, 10))

  const screenSize = useScreenSize()

  const onClickShowMore = () => {
    const nextTenItems = group.literature.slice(displayedLiterature.length, displayedLiterature.length + 10)

    setDisplayedLiterature([...displayedLiterature, ...nextTenItems])
  }

  const onDownloadPdfClick = (literature) => {
    Api.ProductLiterature.download(literature.downloadUrl)
      .then((res) => {
        blobToFileDownload(res, `${group.literature[0].title}.pdf`)
      })
  }

  return (
    <div key={group.literatureType}>
      <S.CollapsiblePanel
        headerComponent={
          <S.GroupHeader>
            {group.literatureType} ({group.literature.length})
          </S.GroupHeader>
        }
      >
        <S.ItemHeader>
          <div>Document Name</div>
          <div>Title</div>
          <div>Language</div>
          <div>Current</div>
          <div>Date Published</div>
        </S.ItemHeader>

        {displayedLiterature.map((literature) => (
          <S.Item key={literature.documentName}>
            <div>{literature.documentName}</div>

            <S.ItemTitle href={literature.downloadUrl} target="_blank" rel="noreferrer">
              {literature.title}
            </S.ItemTitle>

            <div>
              <S.SmallScreenLabel>Language: </S.SmallScreenLabel>
              <span>{literature.language?.toUpperCase()}</span>
            </div>

            <div>
              <S.SmallScreenLabel>Current: </S.SmallScreenLabel>
              <span>{literature.current ? 'Y' : 'N'}</span>
            </div>

            <S.PublishedAtAndDownloadButton>
              <div>{literature.datePublished}</div>
              <S.DownloadLink
                aria-label="csv download link"
              >
                <Button
                  icon="download"
                  text="Download"
                  decoration="secondary"
                  onPress={() => onDownloadPdfClick(literature)}
                  styles={screenSize !== 'large'
                    ? mediumScreenAndDownDownloadButtonStyle
                    : downloadButtonStyle
                  }
                />
              </S.DownloadLink>

            </S.PublishedAtAndDownloadButton>
          </S.Item>
        ))}

        {group.literature.length > 10 && (displayedLiterature.length < group.literature.length) && (
          <Button
            text="Show more"
            onPress={onClickShowMore}
            styles={showMoreButtonStyle}
          />
        )}
      </S.CollapsiblePanel>
    </div>
  )
}

export default ProductLiteratureGroup

const downloadButtonStyle: ComponentStyleSheet<ButtonTheme> = {
  button: {
    borderColor: 'transparent',
    position: 'absolute',
    right: 20,
    top: 4
  },
  text: {
    textTransform: 'capitalize'
  }
}

const mediumScreenAndDownDownloadButtonStyle: ComponentStyleSheet<ButtonTheme> = {
  button: {
    borderColor: 'transparent'
  },
  text: {
    textTransform: 'capitalize'
  }
}

const columnDistribution = '1fr 2fr .5fr .5fr .5fr .5fr'
const showMoreButtonStyle: ComponentStyleSheet<ButtonTheme> = {
  button: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 10
  },
  text: {
    textDecorationLine: 'underline',
    textTransform: 'none'
  }
}

const S = {
  CollapsiblePanel: styled(CollapsiblePanel)`
    background: ${color('background')};
    margin-bottom: 3px;
    padding-left: 10px;
  `,
  GroupHeader: styled.div`
    background: ${color('background')};
    padding: 15px;
    border-radius: 5px;

    font-weight: bold;
  `,
  ItemHeader: styled.div`
    display: grid;
    grid-template-columns: ${columnDistribution};
    padding: 10px;

    @media ${screenSize.medium} {
      display: none;
    }
  `,
  Item: styled.div`
    display: grid;
    grid-template-columns: ${columnDistribution};
    position: relative;
    padding: 10px;

    @media ${screenSize.medium} {
      max-width: 600px;
      display: flex;
      flex-direction: column;
    }
  `,
  PublishedAtAndDownloadButton: styled.div`
    @media ${screenSize.medium} {
      display: flex;
      justify-content: space-between;
    }
  `,
  ItemTitle: styled.a`
    margin-right: 10px;
    color: ${color('primaryText')};
    text-decoration: underline;
    font-weight: bold;

    &:hover {
      color: ${color('primaryText')};
    }
  `,
  SmallScreenLabel: styled.div`
    display: none;
    
    @media ${screenSize.medium} {
      display: inline-block;
      margin-right: 5px;
    }
  `,
  DownloadLink: styled.a`
      margin: 5px 15px;
      display: inline-block;
  `
}
